import { hasAny, nonEmptyBullets, fmtRange, joinContact, joinLinks, getSectionMarkerAttrs } from "./utils";

// ATS Compact: tight spacing, single column, still ATS-safe (no color, plain sans).
export default function ATSCompactTemplate({ resume }) {
  const { personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div
      style={{
        fontFamily: "Calibri, Arial, sans-serif",
        color: "#000000",
        background: "#ffffff",
        padding: "40px 56px",
        fontSize: 9.5,
        lineHeight: 1.35,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 10, borderBottom: "1.5px solid #000", paddingBottom: 6 }}>
        <div data-resume-h1 style={{ fontSize: 18, fontWeight: 700 }}>{personal.fullName || "Your Name"}</div>
        {personal.title && <div style={{ fontSize: 11, marginTop: 1 }}>{personal.title}</div>}
        <div data-resume-contact="true" style={{ fontSize: 9.5, marginTop: 3 }}>
          {[joinContact(personal), joinLinks(personal)].filter(Boolean).join("  |  ")}
        </div>
      </div>

      {summary && <Sec title="Professional Summary"><div>{summary}</div></Sec>}

      {skills?.length > 0 && <Sec title="Skills"><div>{skills.join(" · ")}</div></Sec>}

      {hasAny(experience, ["company", "role"]) && (
        <Sec title="Experience">
          {experience.map((e, i) => {
            const bullets = nonEmptyBullets(e.bullets);
            if (!e.company && !e.role && bullets.length === 0) return null;
            return (
              <div key={e.id || i} data-page-item="true" style={{ marginBottom: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span><b data-resume-jobtitle>{e.role}</b>{e.company ? <>, <span data-resume-company>{e.company}</span></> : ""}{e.location ? <>, <span data-resume-location>{e.location}</span></> : ""}</span>
                  <span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span>
                </div>
                {bullets.length > 0 && (
                  <ul data-resume-workdesc style={{ margin: "1px 0 0 16px", padding: 0 }}>
                    {bullets.map((b, bi) => <li key={`${e.id || i}-b-${bi}`} style={{ marginBottom: 1 }}>{b}</li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </Sec>
      )}

      {hasAny(education, ["school", "degree"]) && (
        <Sec title="Education">
          {education.map((ed, i) => (ed.school || ed.degree) && (
            <div key={ed.id || i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span><b>{ed.school}</b> — {ed.degree}{ed.field ? `, ${ed.field}` : ""}</span>
              <span>{fmtRange(ed.startDate, ed.endDate)}</span>
            </div>
          ))}
        </Sec>
      )}

      {hasAny(projects, ["name"]) && (
        <Sec title="Projects">
          {projects.map((p, i) => p.name && (
            <div key={p.id || i} style={{ marginBottom: 2 }}>
              <b data-resume-projectname>{p.name}</b>{p.tech ? ` — ${p.tech}` : ""}{p.description ? `. ${p.description}` : ""}
            </div>
          ))}
        </Sec>
      )}

      {hasAny(certifications, ["name"]) && (
        <Sec title="Certifications">
          {certifications.map((c, i) => c.name && (
            <div key={c.id || i}><b>{c.name}</b>{c.issuer ? `, ${c.issuer}` : ""}{c.date ? ` — ${c.date}` : ""}</div>
          ))}
        </Sec>
      )}

      {languages?.some((l) => l.name?.trim()) && (
        <Sec title="Languages">
          {languages.map((l) => l.name?.trim() && (
            <div key={l.id}><b>{l.name}</b>{l.level ? ` — ${l.level}` : ""}</div>
          ))}
        </Sec>
      )}
    </div>
  );
}

function Sec({ title, children }) {
  return (
    <section {...getSectionMarkerAttrs(title)} style={{ marginBottom: 8 }}>
      <div data-resume-h2 style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{title}</div>
      {children}
    </section>
  );
}
